import { viewerApi } from '@viewer/lib/viewerApi'
import {
  formatValue,
  getAttributes,
  getCategory,
  getPsetsAndQsets,
  podeTerType,
} from '@viewer/lib/ifcItemData'
import { type ClasseDeTypes, type InstanciaDeType, type TypeLido, agruparTypes, semType } from './agrupar'

/** Ler os types de um modelo aberto, com os seus atributos, property
 *  sets e instâncias. Os types chegam ao motor desde a beta.152: antes o
 *  importador deixava-os de fora. */

export interface TypesDoModelo {
  classes: ClasseDeTypes[]
  /** Elementos que podiam ter type e não têm, por classe. */
  semType: Array<{ categoria: string; ids: number[] }>
  totalTypes: number
}

function eClasseDeType(categoria: string): boolean {
  const c = categoria.toUpperCase()
  if (c === 'IFCRELDEFINESBYTYPE') return false
  return /TYPE$/.test(c) || c === 'IFCDOORSTYLE' || c === 'IFCWINDOWSTYLE'
}

function numero(v: unknown): number | null {
  if (typeof v === 'number') return v
  if (v && typeof v === 'object' && typeof (v as { value?: unknown }).value === 'number') {
    return (v as { value: number }).value
  }
  return null
}

const LOTE = 300

export async function lerTypes(modelId: string): Promise<TypesDoModelo | null> {
  const model = viewerApi.getModel(modelId)
  if (!model) return null
  const baldes = await model.getItemsOfCategories([/TYPE$/i, /STYLE$/i])
  const ids: number[] = []
  for (const [classe, lista] of Object.entries(baldes)) {
    if (eClasseDeType(classe) && Array.isArray(lista)) ids.push(...lista)
  }
  const types: TypeLido[] = []
  for (let i = 0; i < ids.length; i += LOTE) {
    const lote = ids.slice(i, i + LOTE)
    const dados = await model.getItemsData(lote, {
      attributesDefault: true,
      relationsDefault: { attributes: true, relations: false },
      // Os property sets do type estão um nível mais fundo
      // (HasProperties); sem isto vinham só com o nome.
      relations: { HasPropertySets: { attributes: true, relations: true } },
    })
    dados.forEach((d, k) => {
      const t = d as Record<string, unknown>
      const { psets, qsets } = getPsetsAndQsets({ IsDefinedBy: t.HasPropertySets })
      const instancias: InstanciaDeType[] = []
      for (const inst of Array.isArray(t.Types) ? t.Types : []) {
        const io = inst as Record<string, unknown>
        const localId = numero(io._localId)
        if (localId === null) continue
        instancias.push({
          localId,
          categoria: getCategory(io).toUpperCase(),
          nome: formatValue(io.Name),
        })
      }
      types.push({
        localId: lote[k],
        categoria: getCategory(t).toUpperCase(),
        nome: formatValue(t.Name) || '(sem nome)',
        atributos: getAttributes(t),
        psets: [...psets, ...qsets],
        instancias,
      })
    })
  }

  // Quem podia ter type e não tem: só entre os elementos com geometria,
  // que são os que se vêem e se seleccionam.
  const comGeometria = new Set(await model.getItemsIdsWithGeometry())
  const todos = await model.getItemsOfCategories([/.*/])
  const porClasse = new Map<string, number[]>()
  for (const [classe, lista] of Object.entries(todos)) {
    if (!Array.isArray(lista)) continue
    const visiveis = lista.filter((id) => comGeometria.has(id))
    if (visiveis.length > 0) porClasse.set(classe.toUpperCase(), visiveis)
  }
  const comType = new Set(types.flatMap((t) => t.instancias.map((i) => i.localId)))
  return {
    classes: agruparTypes(types),
    semType: semType(porClasse, comType, podeTerType),
    totalTypes: types.length,
  }
}
